/**
 * OCS `listSubscriber` — the request contract and the response shape, in one
 * place both MCP surfaces import.
 *
 * WHY THIS FILE EXISTS
 *   `apps/mcp-server` and `apps/mcp-stdio` each held their own copy of this
 *   logic, and the copies disagreed on the same tool call:
 *
 *     Worker : imsiPrefix / iccidPrefix / msisdnPrefix, status+offset+limit
 *              applied client-side
 *     CLI    : imsi / iccid / msisdn, status+offset FORWARDED to OCS
 *
 *   OCS accepts exactly five properties and rejects the whole request
 *   otherwise ("Unrecognized field status ... 5 known properties"), so a status
 *   filter the Worker quietly ignored would error outright on the CLI.
 *
 *   Both copies also guarded their filters on `Array.isArray(raw)`. The real
 *   response is an ENVELOPE, so that guard was never true and `status`,
 *   `offset` and `limit` all silently did nothing. Measured on the live fleet:
 *   `limit: 2` returned 100 rows, 208,903 characters — while the tool's own
 *   description told callers to always set `limit` to avoid unbounded fetches.
 *
 *   This is the same failure class as ocs-param-shapes.ts, which exists because
 *   "prose comments at each call site did not stop it". One shared
 *   implementation is the enforcement.
 */

/**
 * The ONLY properties OCS `listSubscriber` accepts.
 *
 * Anything else fails the entire request. Proven against the live server, not
 * inferred from ocs-methods.json (see the note in ocs-param-shapes.ts about why
 * that file cannot answer this).
 */
export const OCS_LIST_SUBSCRIBER_PARAMS = [
  "accountId",
  "activationCode",
  "imsiPrefix",
  "iccidPrefix",
  "msisdnPrefix",
] as const;

/** Caller-facing filters, as the MCP tool exposes them. */
export interface ListSubscriberArgs {
  imsi?: string;
  iccid?: string;
  activationCode?: string;
  accountId?: number;
  msisdn?: string;
  status?: string;
  offset?: number;
  limit?: number;
}

/**
 * Map tool args to OCS params.
 *
 * `status`, `offset` and `limit` are deliberately absent from the result: OCS
 * cannot take them, and they are applied to the returned rows instead.
 *
 * The *Prefix names matter — OCS matches them as PREFIXES, not exact values. A
 * full ICCID still matches, being a prefix of itself, but a partial value
 * legitimately returns several rows.
 */
export function buildListSubscriberParams(
  args: ListSubscriberArgs,
): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (args.imsi) params.imsiPrefix = args.imsi;
  if (args.iccid) params.iccidPrefix = args.iccid;
  if (args.activationCode) params.activationCode = args.activationCode;
  if (args.accountId !== undefined) params.accountId = args.accountId;
  if (args.msisdn) params.msisdnPrefix = args.msisdn;
  return params;
}

/**
 * Read a subscriber row's status name.
 *
 * OCS returns `status` as an ARRAY of records whose own `status` field is the
 * name ("Active" / "Inventory" / "Inactive" / "End of Life"), not a plain
 * string. Comparing the array against a string silently matches nothing, so a
 * filter written against `row.status` quietly returns zero rows.
 *
 * The array is a HISTORY: closed periods carry an `endDate`, the current one
 * does not. OCS happens to return it newest-first today, but that is ordering
 * we do not control, so prefer the open period explicitly and only fall back
 * to the first readable entry. A subscriber whose history begins with a closed
 * "Manually Suspended" period is Active, and must not read as suspended.
 */
export function extractSubscriberStatus(row: unknown): string | null {
  if (row === null || typeof row !== "object") return null;
  const raw = (row as Record<string, unknown>).status;
  if (typeof raw === "string" && raw.length > 0) return raw;
  if (!Array.isArray(raw)) return null;

  let fallback: string | null = null;
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const rec = entry as Record<string, unknown>;
    const name = rec.status;
    if (typeof name !== "string" || name.length === 0) continue;

    const endDate = rec.endDate;
    const open = endDate === undefined || endDate === null || endDate === "";
    if (open) return name;
    if (fallback === null) fallback = name;
  }
  return fallback;
}

/**
 * Pull subscriber rows out of whatever shape listSubscriber returned.
 *
 * Live shape is `{hasMore, nbFound, subscriberList: [...]}`. Returns
 * `rows: null` for anything unrecognised so callers pass the payload through
 * rather than inventing an empty list.
 */
export function unwrapSubscriberList(raw: unknown): {
  rows: unknown[] | null;
  envelope: Record<string, unknown> | null;
} {
  if (Array.isArray(raw)) return { rows: raw, envelope: null };
  if (raw !== null && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.subscriberList)) {
      return { rows: obj.subscriberList as unknown[], envelope: obj };
    }
  }
  return { rows: null, envelope: null };
}

/**
 * Put filtered rows back into the caller's original shape, with honest counts.
 *
 * `nbFound` becomes the number of rows that MATCHED the filters — not OCS's
 * original total and not the page size. Reporting `nbFound: 100` beside 2 rows
 * is how a truncated result gets read as a complete one.
 *
 * Adds `truncated` + `note` when rows were dropped, naming the params that
 * would narrow the request: the same never-silent rule as bounded-results.ts.
 */
export function rewrapSubscriberList(
  raw: unknown,
  envelope: Record<string, unknown> | null,
  rows: unknown[] | null,
  filtered: unknown[] | null,
  matched: number | undefined,
): unknown {
  if (rows === null || filtered === null) return raw;

  const total = matched ?? filtered.length;
  const dropped = total - filtered.length;

  // Caller originally got a bare array; keep giving them one.
  if (envelope === null) return filtered;

  const out: Record<string, unknown> = {
    ...envelope,
    subscriberList: filtered,
    nbFound: total,
    hasMore: filtered.length < total,
  };

  if (dropped > 0) {
    out.truncated = true;
    out.note =
      `${dropped} of ${total} matching subscribers omitted by offset/limit. ` +
      `Narrow with \`status\`, \`accountId\` or \`iccid\`, or page with \`offset\`.`;
  }

  return out;
}

/**
 * The whole client-side filter pipeline: status -> offset -> limit, re-wrapped.
 *
 * Both MCP surfaces call this, so they cannot drift on filter ORDER either —
 * offset-before-limit is what a caller paging through results expects.
 */
export function applySubscriberFilters(
  raw: unknown,
  args: ListSubscriberArgs,
): unknown {
  const { rows, envelope } = unwrapSubscriberList(raw);
  let filtered = rows;

  if (filtered && args.status) {
    const wanted = String(args.status).trim().toLowerCase();
    filtered = filtered.filter((row) => {
      const status = extractSubscriberStatus(row);
      return status !== null && status.toLowerCase() === wanted;
    });
  }

  const matched = filtered?.length;

  if (filtered && typeof args.offset === "number" && args.offset > 0) {
    filtered = filtered.slice(args.offset);
  }
  if (filtered && typeof args.limit === "number" && args.limit >= 0) {
    filtered = filtered.slice(0, args.limit);
  }

  return rewrapSubscriberList(raw, envelope, rows, filtered, matched);
}
